// frontend/src/pages/BulkInvite.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
    UploadIcon,
    DownloadIcon,
    LinkIcon,
    ClipboardIcon,
    CheckIcon,
    XIcon,
    UsersIcon,
    MailIcon,
    RefreshIcon,
    PlusIcon,
    UserAddIcon
} from '@heroicons/react/outline';
import { QRCodeSVG } from 'qrcode.react';
import { getAssessments, bulkInvite, getDepartments, generateStandardLinks, createDepartment, sendEmailInvites, sendSingleEmailInvite, sendStandardLinkEmail } from '../services/adminApi';
import { showSuccess, showError, showWarning } from '../utils/toastUtils';

// Custom QR Code Icon
const QrCodeIcon = ({ className = "h-5 w-5" }) => (
    <svg
        className={className}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h-2m2 0h2m-6-3v3m0 0h-2m2 0h2m6-3v3m0 0h-2m2 0h2M4 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h-2m2 0h2m6-3v3m0 0h-2m2 0h2"
        />
    </svg>
);

const BulkInvite = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState('');
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [inviteCount, setInviteCount] = useState(0);
    const [generatedLink, setGeneratedLink] = useState(null);
    const [showLinks, setShowLinks] = useState(false);
    const [copied, setCopied] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [showAddDepartment, setShowAddDepartment] = useState(false);
    const [newDepartment, setNewDepartment] = useState({ name: '', code: '', description: '' });
    const [addingDepartment, setAddingDepartment] = useState(false);
    const qrCodeRef = useRef(null);

    // Email the generated standard link
    const [showEmailLinkModal, setShowEmailLinkModal] = useState(false);
    const [emailLinkTo, setEmailLinkTo] = useState('');
    const [emailLinkName, setEmailLinkName] = useState('');
    const [emailLinkSending, setEmailLinkSending] = useState(false);
    const [emailLinkSuccess, setEmailLinkSuccess] = useState(null);

    // User Role State
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [organizationId, setOrganizationId] = useState(null);

    // Manual Email Input States
    const [showManualEmail, setShowManualEmail] = useState(false);
    const [manualEmail, setManualEmail] = useState('');
    const [manualName, setManualName] = useState('');
    const [manualDepartment, setManualDepartment] = useState('');
    const [manualSuccess, setManualSuccess] = useState(null);

    // ============================================
    // GET USER DATA FROM LOCAL STORAGE
    // ============================================
    const getUserData = () => {
        const userData = localStorage.getItem('adminUser');
        console.log('📋 Raw userData from localStorage:', userData);

        if (userData) {
            try {
                const user = JSON.parse(userData);
                console.log('📋 Parsed user object:', user);

                // Try multiple possible field names
                const orgId = user.organization_id || user.organizationId || user.org_id || user.orgId;
                const isSuper = user.role === 'super_admin';

                console.log('📋 User role:', user.role);
                console.log('📋 Is Super Admin:', isSuper);
                console.log('📋 Organization ID found:', orgId);
                console.log('📋 All user keys:', Object.keys(user));

                return { user, orgId, isSuper };
            } catch (e) {
                console.error('Error parsing user data:', e);
                return { user: null, orgId: null, isSuper: false };
            }
        }

        console.warn('⚠️ No adminUser found in localStorage');
        return { user: null, orgId: null, isSuper: false };
    };

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        console.log('🔑 Admin token exists:', token ? 'Yes' : 'No');
        if (token) {
            console.log('🔑 Token preview:', token.substring(0, 20) + '...');
        }
        // Get user data and set state
        const { user, orgId, isSuper } = getUserData();

        setIsSuperAdmin(isSuper);
        setUserRole(user?.role || '');
        setOrganizationId(orgId || null);

        // Fetch assessments with the orgId directly
        fetchAssessments(orgId, isSuper);
    }, []);

    useEffect(() => {
        if (selectedAssessment) {
            fetchDepartments();
        }
    }, [selectedAssessment]);

    // ============================================
    // FETCH ASSESSMENTS - WITH ORG ID PARAMETER
    // ============================================
    const fetchAssessments = async (orgId = null, isSuper = null) => {
        try {
            // If parameters not provided, get from state or localStorage
            let effectiveOrgId = orgId;
            let effectiveIsSuper = isSuper;

            if (effectiveOrgId === null || effectiveIsSuper === null) {
                const { orgId: localOrgId, isSuper: localIsSuper } = getUserData();
                effectiveOrgId = localOrgId;
                effectiveIsSuper = localIsSuper;
            }

            console.log('📋 [fetchAssessments] Effective Org ID:', effectiveOrgId);
            console.log('📋 [fetchAssessments] Effective Is Super:', effectiveIsSuper);

            const response = await getAssessments();
            const allAssessments = response.data.assessments || [];
            console.log('📋 [fetchAssessments] Total assessments from API:', allAssessments.length);
            console.log('📋 [fetchAssessments] All assessments:', allAssessments.map(a => ({
                id: a.id,
                name: a.name,
                org: a.organization_id
            })));

            // Filter assessments based on user role
            let filteredAssessments = [];

            if (effectiveIsSuper) {
                // Super Admin - show all assessments
                filteredAssessments = allAssessments;
                console.log('📋 Super Admin - All assessments:', filteredAssessments.length);
            } else if (effectiveOrgId) {
                // Organization Admin - only show assessments assigned to their organization
                filteredAssessments = allAssessments.filter(a => a.organization_id === effectiveOrgId);
                console.log('📋 Organization Admin - Filtered assessments:', filteredAssessments.length);
                console.log('📋 Organization ID:', effectiveOrgId);

                // Log which assessments were filtered out
                const filteredOut = allAssessments.filter(a => a.organization_id !== effectiveOrgId);
                if (filteredOut.length > 0) {
                    console.log('📋 Filtered out assessments:', filteredOut.map(a => a.id));
                }
            } else {
                // No organization ID found
                filteredAssessments = [];
                console.warn('⚠️ No organization ID found for non-super admin');
            }

            setAssessments(filteredAssessments);

            // Auto-select first assessment if available
            if (filteredAssessments.length > 0) {
                setSelectedAssessment(filteredAssessments[0].id);
                console.log('📋 Auto-selected assessment:', filteredAssessments[0].id);
            } else {
                setSelectedAssessment('');
                if (!effectiveIsSuper) {
                    showWarning('No assessments assigned to your organization. Please contact your administrator.');
                } else {
                    showWarning('No assessments available. Please import an assessment first.');
                }
            }
        } catch (error) {
            console.error('Error fetching assessments:', error);
            showError('Failed to load assessments');
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await getDepartments(selectedAssessment);
            setDepartments(response.data.departments || []);
        } catch (error) {
            console.error('Error fetching departments:', error);
            setDepartments([]);
        }
    };

    const handleAddDepartment = async () => {
        if (!newDepartment.name || !newDepartment.code) {
            setError('Department name and code are required');
            showWarning('Department name and code are required');
            return;
        }

        setAddingDepartment(true);
        setError(null);

        try {
            const response = await createDepartment({
                assessment_id: selectedAssessment,
                name: newDepartment.name,
                code: newDepartment.code.toUpperCase(),
                description: newDepartment.description || '',
                is_active: true
            });

            if (response.data.success) {
                await fetchDepartments();
                setShowAddDepartment(false);
                setNewDepartment({ name: '', code: '', description: '' });
                setSelectedDepartment(response.data.department.id);
                showSuccess('Department added successfully!');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create department');
            showError('Failed to create department');
        } finally {
            setAddingDepartment(false);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xlsm'))) {
            setFile(selectedFile);
            setError(null);
            setResults(null);
            setShowLinks(false);
        } else {
            setError('Please select a valid CSV or Excel file');
            showError('Invalid file format');
        }
    };

    // ============================================
    // EMAIL INVITE - UPLOAD & SEND
    // ============================================
    const handleUploadAndInvite = async () => {
        if (!file) {
            setError('Please select a file first');
            showWarning('Please select a file first');
            return;
        }

        if (!selectedAssessment) {
            setError('Please select an assessment');
            showWarning('Please select an assessment');
            return;
        }

        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('assessment_id', selectedAssessment);
        if (selectedDepartment) {
            formData.append('department_id', selectedDepartment);
        }
        formData.append('send_emails', 'true');

        try {
            const response = await bulkInvite(formData);
            setResults(response.data);
            setInviteCount(response.data.results?.length || 0);

            showSuccess(`✅ ${response.data.results?.length || 0} email invitations sent!`);
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to process invitations');
            showError('Failed to send invitations');
        } finally {
            setUploading(false);
        }
    };

    // ============================================
    // STANDARD LINK - GENERATE SINGLE LINK
    // ============================================
    const generateStandardLink = async () => {
        if (!selectedAssessment) {
            setError('Please select an assessment');
            showWarning('Please select an assessment first');
            return;
        }

        // Debug: Check token before making the request
        const token = localStorage.getItem('adminToken');
        console.log('🔑 Token before request:', token ? 'Found' : 'Not found');
        if (token) {
            console.log('🔑 Token preview:', token.substring(0, 20) + '...');
        }
        setGenerating(true);
        setError(null);

        try {
            const response = await generateStandardLinks({
                assessment_id: selectedAssessment,
                department_id: selectedDepartment || null,
                count: 1,
                skip_demographics: true
            });

            const linkData = response.data.links?.[0];
            if (linkData) {
                setGeneratedLink(linkData);
                setShowLinks(true);

                // Auto-copy to clipboard
                await navigator.clipboard.writeText(linkData.standard_link);
                setCopied(true);

                showSuccess('✅ Standard link generated and copied to clipboard!');
            }
        } catch (error) {
            console.error('Error generating link:', error);
            setError('Failed to generate standard link');
            showError('Failed to generate standard link');
        } finally {
            setGenerating(false);
        }
    };

    // ============================================
    // SEND STANDARD LINK VIA EMAIL
    // ============================================
    const handleSendStandardLinkEmail = async () => {
        if (!generatedLink?.phone_token) {
            showError('No standard link generated yet');
            return;
        }
        if (!emailLinkTo) {
            showWarning('Please enter an email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailLinkTo)) {
            showError('Invalid email address');
            return;
        }

        setEmailLinkSending(true);
        setError(null);
        setEmailLinkSuccess(null);

        try {
            const response = await sendStandardLinkEmail({
                phone_token: generatedLink.phone_token,
                email: emailLinkTo,
                name: emailLinkName.trim() || emailLinkTo.split('@')[0],
                assessment_id: selectedAssessment,
                department_id: selectedDepartment || null,
            });

            if (response.data.success) {
                setEmailLinkSuccess({
                    email: response.data.sent_to,
                    name: response.data.candidate_name,
                });
                setEmailLinkTo('');
                setEmailLinkName('');
                showSuccess(`✅ Standard link emailed to ${response.data.sent_to}`);
            }
        } catch (err) {
            console.error('Error sending standard link email:', err);
            setError(err.response?.data?.error || 'Failed to send email');
            showError('Failed to send email');
        } finally {
            setEmailLinkSending(false);
        }
    };

    const openEmailLinkModal = () => {
        setShowEmailLinkModal(true);
        setEmailLinkSuccess(null);
        setEmailLinkTo('');
        setEmailLinkName('');
    };

    const closeEmailLinkModal = () => {
        setShowEmailLinkModal(false);
        setEmailLinkSuccess(null);
    };

    // ============================================
    // DOWNLOAD QR CODE
    // ============================================
    // ============================================
    // DOWNLOAD QR CODE — from QRCodeSVG
    // ============================================
    const downloadQRCode = async () => {
        if (!qrCodeRef.current) {
            showError('QR code not ready');
            return;
        }

        // Find the SVG element inside the wrapper
        const svg = qrCodeRef.current.querySelector('svg');
        if (!svg) {
            showError('QR code SVG not found');
            return;
        }

        try {
            // Serialize the SVG into a data URL
            const svgData = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            // Read the SVG dimensions (fallback to 140 if missing)
            const width = parseInt(svg.getAttribute('width')) || 140;
            const height = parseInt(svg.getAttribute('height')) || 140;

            // Draw SVG onto a canvas
            const img = new Image();
            img.crossOrigin = 'anonymous';

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
            });

            const canvas = document.createElement('canvas');
            // Upscale for a crisp QR (2x)
            const scale = 2;
            canvas.width = width * scale;
            canvas.height = height * scale;

            const ctx = canvas.getContext('2d');
            // White background so it doesn't come out transparent
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Cleanup the blob URL
            URL.revokeObjectURL(url);

            // Trigger download
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `qrcode_${generatedLink?.phone_token || 'link'}.png`;
            link.href = dataUrl;
            link.click();

            showSuccess('✅ QR Code downloaded!');
        } catch (err) {
            console.error('Error downloading QR code:', err);
            showError('Failed to download QR code');
        }
    };

    // ============================================
    // SEND SINGLE EMAIL INVITE (Manual Entry)
    // ============================================
    const handleSendSingleEmailInvite = async () => {
        if (!selectedAssessment) {
            setError('Please select an assessment');
            showWarning('Please select an assessment first');
            return;
        }

        if (!manualEmail) {
            setError('Please enter an email address');
            showWarning('Please enter an email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(manualEmail)) {
            setError('Please enter a valid email address');
            showError('Invalid email address');
            return;
        }

        const candidateName = manualName.trim() || manualEmail.split('@')[0];

        setSendingEmails(true);
        setError(null);
        setManualSuccess(null);

        try {
            const response = await sendSingleEmailInvite({
                assessment_id: selectedAssessment,
                email: manualEmail,
                name: candidateName,
                department_id: selectedDepartment || null,
                department_name: manualDepartment || null
            });

            if (response.data.success) {
                setManualSuccess({
                    email: manualEmail,
                    name: candidateName,
                    access_token: response.data.access_token
                });

                setManualEmail('');
                setManualName('');
                setManualDepartment('');

                showSuccess('✅ Invitation sent successfully!');
            }
        } catch (error) {
            console.error('Error sending single email invite:', error);
            setError(error.response?.data?.error || 'Failed to send invitation');
            showError('Failed to send invitation');
        } finally {
            setSendingEmails(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        showSuccess('✅ Link copied to clipboard!');
        setTimeout(() => setCopied(false), 3000);
    };

    const downloadTemplate = () => {
        const headers = ['email', 'name', 'assessment_id', 'department_code', 'department_name'];
        let csv = headers.join(',') + '\n';
        csv += `john.doe@example.com,John Doe,${selectedAssessment || 'EBCSS-00001'},PROD,\n`;
        csv += `jane.smith@example.com,Jane Smith,${selectedAssessment || 'EBCSS-00001'},,Human Resources\n`;

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_invite_template_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const toggleManualEmail = () => {
        setShowManualEmail(!showManualEmail);
        setManualSuccess(null);
        setError(null);
    };

    // Get organization name for display
    const getOrganizationName = () => {
        return organizationId || 'N/A';
    };

    return (
        <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-xl font-bold text-[#063b56]">Bulk Invite Candidates</h1>
                            <p className="text-sm text-gray-500">Invite candidates via email or generate a standard link</p>
                        </div>
                        <div>
                            {!isSuperAdmin && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                    Organization Admin
                                </span>
                            )}
                            {isSuperAdmin && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                    Super Admin
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {/* {!isSuperAdmin && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>Organization:</strong> {getOrganizationName()}
                                <span className="ml-3 text-xs text-blue-500">
                                    Showing assessments assigned to your organization only
                                </span>
                            </p>
                        </div>
                    )} */}

                    {/* Assessment Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Assessment <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedAssessment}
                                onChange={(e) => setSelectedAssessment(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                            >
                                <option value="">Select Assessment</option>
                                {assessments.map((assessment) => (
                                    <option key={assessment.id} value={assessment.id}>
                                        {assessment.name}
                                    </option>
                                ))}
                            </select>
                            {assessments.length === 0 && !isSuperAdmin && (
                                <p className="text-xs text-yellow-600 mt-1">
                                    ⚠️ No assessments assigned to your organization. Please contact your administrator.
                                </p>
                            )}
                            {assessments.length === 0 && isSuperAdmin && (
                                <p className="text-xs text-yellow-600 mt-1">
                                    No assessments found.
                                </p>
                            )}
                            {/* {assessments.length > 0 && !isSuperAdmin && (
                                <p className="text-xs text-green-600 mt-1">
                                    ✅ {assessments.length} assessment(s) available for your organization
                                </p>
                            )} */}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Department (Optional)
                            </label>
                            <div className="flex gap-2">
                                <select
                                    value={selectedDepartment}
                                    onChange={(e) => setSelectedDepartment(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                    disabled={!selectedAssessment}
                                >
                                    <option value="">No Department</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={dept.id}>
                                            {dept.name} ({dept.code})
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => setShowAddDepartment(true)}
                                    className="px-3 py-2 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a] flex items-center whitespace-nowrap"
                                    title="Add Department"
                                    disabled={!selectedAssessment}
                                >
                                    <PlusIcon className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {/* ============================================ */}
                    {/* SECTION 2: STANDARD LINK */}
                    {/* ============================================ */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <QrCodeIcon className="h-5 w-5 text-[#063b56]" />
                            <h2 className="text-lg font-semibold text-[#063b56]">Standard Link</h2>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Single Link for All</span>
                        </div>

                        <button
                            onClick={generateStandardLink}
                            disabled={!selectedAssessment || generating}
                            className="px-6 py-2 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a] disabled:opacity-50 flex items-center shadow-sm text-sm"
                        >
                            {generating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <QrCodeIcon className="h-5 w-5 mr-2" />
                                    Generate Standard Link
                                </>
                            )}
                        </button>

                        {/* Standard Link Display */}
                        {showLinks && generatedLink && (
                            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 mb-3">
                                    <LinkIcon className="h-5 w-5 text-green-600" />
                                    <span className="font-medium text-green-700">Link Generated</span>
                                </div>

                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-white rounded-lg border border-gray-200 p-2">
                                                <p className="text-xs text-gray-600 break-all font-mono">
                                                    {generatedLink.standard_link}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => copyToClipboard(generatedLink.standard_link)}
                                                className="px-3 py-2 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a] flex items-center whitespace-nowrap text-sm"
                                            >
                                                <ClipboardIcon className="h-4 w-4 mr-1" />
                                                {copied ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                        <div className="mt-2 flex gap-4 text-xs text-gray-500">
                                            <span><strong>Token:</strong> {generatedLink.phone_token}</span>
                                            <span><strong>Dept:</strong> {generatedLink.department_name || 'N/A'}</span>

                                        </div>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div ref={qrCodeRef} className="bg-white p-2 rounded-lg border border-gray-200">
                                            <QRCodeSVG
                                                value={generatedLink.standard_link}
                                                size={140}
                                                level="H"
                                                includeMargin={true}
                                                bgColor="#ffffff"
                                                fgColor="#063b56"
                                            />
                                        </div>
                                        <button
                                            onClick={downloadQRCode}
                                            className="mt-2 px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 flex items-center"
                                        >
                                            <DownloadIcon className="h-3 w-3 mr-1" />
                                            Download QR
                                        </button>
                                        <button
                                            onClick={openEmailLinkModal}
                                            className="mt-3 px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] flex items-center justify-center gap-2 text-sm w-full shadow-sm"
                                        >
                                            <MailIcon className="h-4 w-4" />
                                            Email this link
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Divider */}
                    {/* <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center">
                            <span className="bg-white px-4 text-xs text-gray-400">OR</span>
                        </div>
                    </div> */}

                    {/* ============================================ */}
                    {/* SECTION 1: EMAIL INVITE */}
                    {/* ============================================ */}
                    {/* <div className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <MailIcon className="h-5 w-5 text-[#063b56]" />
                            <h2 className="text-lg font-semibold text-[#063b56]">Email Invite</h2>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Bulk Upload</span>
                        </div>

                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#ff9f2f] transition-colors relative">
                            <UploadIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600 text-sm">
                                {file ? file.name : 'Click to select or drag and drop CSV/Excel file'}
                            </p>
                            <p className="text-xs text-gray-400">Supported formats: .csv, .xlsx, .xlsm</p>
                            <input
                                type="file"
                                accept=".csv,.xls,.xlsx,.xlsm"
                                onChange={handleFileSelect}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                        </div>

                        {file && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                                <div>
                                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({(file.size / 1024).toFixed(1)} KB)</span>
                                </div>
                                <button
                                    onClick={() => setFile(null)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <XIcon className="h-5 w-5" />
                                </button>
                            </div>
                        )}

                        <div className="mt-4 flex gap-3">
                            <button
                                onClick={downloadTemplate}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center text-sm"
                            >
                                <DownloadIcon className="h-4 w-4 mr-2" />
                                Download Template
                            </button>
                            <button
                                onClick={handleUploadAndInvite}
                                disabled={!file || !selectedAssessment || uploading}
                                className="px-6 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center shadow-sm text-sm"
                            >
                                {uploading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <MailIcon className="h-4 w-4 mr-2" />
                                        Send Emails
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="mt-4">
                            <button
                                onClick={toggleManualEmail}
                                className="text-sm text-[#063b56] hover:text-[#ff9f2f] transition-colors flex items-center"
                            >
                                <UserAddIcon className="h-4 w-4 mr-1" />
                                {showManualEmail ? 'Hide Manual Entry' : '+ Send Single Email'}
                            </button>

                            {showManualEmail && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={manualEmail}
                                                onChange={(e) => setManualEmail(e.target.value)}
                                                placeholder="candidate@example.com"
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                                disabled={sendingEmails}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Name (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={manualName}
                                                onChange={(e) => setManualName(e.target.value)}
                                                placeholder="John Doe"
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                                disabled={sendingEmails}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Department (Optional)
                                            </label>
                                            <select
                                                value={manualDepartment}
                                                onChange={(e) => setManualDepartment(e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                                disabled={sendingEmails || !selectedAssessment}
                                            >
                                                <option value="">Select Department</option>
                                                {departments.map((dept) => (
                                                    <option key={dept.id} value={dept.name}>
                                                        {dept.name} ({dept.code})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={handleSendSingleEmailInvite}
                                                disabled={!selectedAssessment || !manualEmail || sendingEmails}
                                                className="w-full px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center justify-center shadow-sm text-sm"
                                            >
                                                {sendingEmails ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MailIcon className="h-4 w-4 mr-2" />
                                                        Send Invite
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {manualSuccess && (
                                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-start">
                                                <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm text-green-700 font-medium">Invitation sent!</p>
                                                    <p className="text-xs text-green-600">
                                                        {manualSuccess.email} • {manualSuccess.name}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {results && (
                        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                                <div className="flex items-center text-green-700">
                                    <MailIcon className="h-5 w-5 mr-2" />
                                    <span className="font-medium">
                                        {results.successful || results.results?.length || 0} email invitations sent
                                    </span>
                                    {results.failed > 0 && (
                                        <span className="ml-2 text-red-600">({results.failed} failed)</span>
                                    )}
                                </div>
                            </div>

                            {results.errors && results.errors.length > 0 && (
                                <div className="px-6 py-4">
                                    <h4 className="text-sm font-semibold text-red-600 mb-2">Failed</h4>
                                    <div className="max-h-40 overflow-y-auto">
                                        {results.errors.map((err, idx) => (
                                            <div key={idx} className="text-sm text-red-600 py-1 border-b border-red-50">
                                                {err.email}: {err.error}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )} */}
                </div>
            </div>

            {/* Add Department Modal */}
            {
                showAddDepartment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-[#063b56]">Add Department</h3>
                                <button
                                    onClick={() => setShowAddDepartment(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Department Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newDepartment.name}
                                        onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                                        placeholder="e.g., Production"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Department Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newDepartment.code}
                                        onChange={(e) => setNewDepartment({ ...newDepartment, code: e.target.value.toUpperCase() })}
                                        placeholder="e.g., PROD"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                        maxLength={10}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Unique code (max 10 chars)</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={newDepartment.description}
                                        onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
                                        placeholder="Brief description"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                    />
                                </div>

                                <div className="flex justify-end space-x-3 pt-2">
                                    <button
                                        onClick={() => setShowAddDepartment(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel                                </button>
                                    <button
                                        onClick={handleAddDepartment}
                                        disabled={addingDepartment || !newDepartment.name || !newDepartment.code}
                                        className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center"
                                    >
                                        {addingDepartment ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <PlusIcon className="h-4 w-4 mr-1" />
                                                Add
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {showEmailLinkModal && generatedLink && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                                <MailIcon className="h-5 w-5 text-[#063b56]" />
                                <h3 className="text-lg font-bold text-[#063b56]">
                                    Email Standard Link
                                </h3>
                            </div>
                            <button
                                onClick={closeEmailLinkModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Link preview */}
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Link to send:</p>
                            <p className="text-xs text-gray-700 break-all font-mono">
                                {generatedLink.standard_link}
                            </p>
                        </div>

                        {/* Email input */}
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={emailLinkTo}
                                    onChange={(e) => setEmailLinkTo(e.target.value)}
                                    placeholder="candidate@example.com"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                    disabled={emailLinkSending}
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={emailLinkName}
                                    onChange={(e) => setEmailLinkName(e.target.value)}
                                    placeholder="John Doe"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f]"
                                    disabled={emailLinkSending}
                                />
                            </div>

                            {/* Success message */}
                            {emailLinkSuccess && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start">
                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-green-700 font-medium">
                                                Email sent!
                                            </p>
                                            <p className="text-xs text-green-600">
                                                {emailLinkSuccess.email} • {emailLinkSuccess.name}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error */}
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                    {error}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    onClick={closeEmailLinkModal}
                                    disabled={emailLinkSending}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {emailLinkSuccess ? 'Done' : 'Cancel'}
                                </button>
                                <button
                                    onClick={handleSendStandardLinkEmail}
                                    disabled={emailLinkSending || !emailLinkTo}
                                    className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center gap-2"
                                >
                                    {emailLinkSending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <MailIcon className="h-4 w-4" />
                                            Send Email
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default BulkInvite;